import { Component, OnInit } from '@angular/core';
import { AppConfigService } from 'src/app/services/app-config/app-config.service';
import * as alertifyjs from 'alertifyjs';

const VALID_EVAL_MODES = ['BOTH', 'EITHER', 'SCORE_ONLY', 'PROBABILITY_ONLY'] as const;
type EvaluationMode = typeof VALID_EVAL_MODES[number];

interface CredolabConfigData {
    minScore: number | null;
    maxProbability: number | null;
    requireBoth: boolean | null;
    evaluationMode: EvaluationMode | null;
    autoRefuseFlags: Record<string, boolean>;
    availableFlags: string[];
    updatedBy: string | null;
    updatedAt: string | null;
}

@Component({
    selector: 'app-credolab-thresholds',
    templateUrl: './credolab-thresholds.component.html',
    styleUrls: ['./credolab-thresholds.component.css']
})
export class CredolabThresholdsComponent implements OnInit {

    credolabData: CredolabConfigData | null = null;

    credolabLoading = false;
    credolabSaving = false;
    credolabNotSeeded = false;

    thresholdsForm = {
        minScore: null as number | null,
        maxProbability: null as number | null,
        evaluationMode: null as EvaluationMode | null,
    };

    constructor(private appConfigService: AppConfigService) {}

    ngOnInit(): void {
        this.loadCredolab();
    }

    async loadCredolab(): Promise<void> {
        this.credolabLoading = true;
        try {
            const res = await this.appConfigService.getCredolabConfig();
            this.credolabData = res?.data || res;
            this.thresholdsForm = {
                minScore: this.credolabData?.minScore ?? null,
                maxProbability: this.credolabData?.maxProbability ?? null,
                evaluationMode: this.credolabData?.evaluationMode ?? 'BOTH',
            };
            this.credolabNotSeeded = false;
        } catch (error: any) {
            if (error?.status === 404) {
                this.credolabNotSeeded = true;
                this.credolabData = null;
            } else {
                alertifyjs.error(`Error cargando config: ${error?.error?.message || error?.message || 'desconocido'}`);
            }
        } finally {
            this.credolabLoading = false;
        }
    }

    async saveThresholds(): Promise<void> {
        const payload: any = {};
        if (this.thresholdsForm.minScore !== null && this.thresholdsForm.minScore !== this.credolabData?.minScore) payload.minScore = this.thresholdsForm.minScore;
        if (this.thresholdsForm.maxProbability !== null && this.thresholdsForm.maxProbability !== this.credolabData?.maxProbability) payload.maxProbability = this.thresholdsForm.maxProbability;
        if (this.thresholdsForm.evaluationMode !== null && this.thresholdsForm.evaluationMode !== this.credolabData?.evaluationMode) payload.evaluationMode = this.thresholdsForm.evaluationMode;

        if (Object.keys(payload).length === 0) {
            alertifyjs.message('No hay cambios para guardar.');
            return;
        }

        if (payload.minScore !== undefined && (payload.minScore < 0 || payload.minScore > 1000)) {
            alertifyjs.warning('minScore debe estar entre 0 y 1000');
            return;
        }
        if (payload.maxProbability !== undefined && (payload.maxProbability < 0 || payload.maxProbability > 1)) {
            alertifyjs.warning('maxProbability debe estar entre 0 y 1');
            return;
        }
        if (payload.evaluationMode !== undefined
            && !VALID_EVAL_MODES.includes(payload.evaluationMode)) {
            alertifyjs.warning('Modo de evaluación inválido');
            return;
        }

        const confirmed = window.confirm(
            `¿Actualizar umbrales Credolab?\n` +
            `minScore=${this.thresholdsForm.minScore}\n` +
            `maxProbability=${this.thresholdsForm.maxProbability}\n` +
            `Modo: ${this.getEvaluationModeLabel(this.thresholdsForm.evaluationMode)}`
        );
        if (!confirmed) return;

        this.credolabSaving = true;
        try {
            const res = await this.appConfigService.updateCredolabThresholds(payload);
            if (res?.data?.unchanged) {
                alertifyjs.message('Los valores ya eran los solicitados.');
            } else {
                alertifyjs.success('Umbrales actualizados');
            }
            await this.loadCredolab();
        } catch (error: any) {
            alertifyjs.error(error?.error?.message || error?.message || 'Error al guardar');
        } finally {
            this.credolabSaving = false;
        }
    }

    getEvaluationModeLabel(mode: EvaluationMode | null): string {
        switch (mode) {
            case 'BOTH':             return 'Ambos umbrales deben pasar';
            case 'EITHER':           return 'Basta con que uno pase';
            case 'SCORE_ONLY':       return 'Solo score decide';
            case 'PROBABILITY_ONLY': return 'Solo probabilidad decide';
            default:                 return '—';
        }
    }
}
